import { Link } from "react-router-dom";
import { Wrench, Search, MonitorSmartphone, ShieldCheck, Headphones, Printer, FileText, ArrowRight } from "lucide-react";

const REPAIRS = ["Écran & tactile", "Batterie", "Caméra", "Connecteur de charge", "Vitre arrière", "Haut-parleur & micro", "Oxydation", "Logiciel & données"];

export default function Services() {
  return (
    <div className="container-oxy py-10 sm:py-14" data-testid="services-page">
      <p className="eyebrow">Nos services</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Bien plus qu'une boutique</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-600 sm:text-base">
        Atelier de réparation, conseil, et services du quotidien — tout au même endroit.
      </p>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        <div className="card-oxy p-6 sm:p-8" data-testid="service-repair">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-gold"><Wrench className="h-6 w-6" /></span>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Réparation smartphone</h2>
          <p className="mt-2 text-sm text-slate-600">iPhone, Samsung, Xiaomi, Google Pixel et plus. La plupart des réparations en moins d'1h30.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {REPAIRS.map((r) => (
              <span key={r} className="rounded-full bg-gold-soft px-3 py-1.5 text-xs font-semibold text-gold-dark">{r}</span>
            ))}
          </div>
          <Link to="/reparation" data-testid="service-repair-cta" className="btn-gold mt-6 !px-5 !py-2.5 text-sm">
            Devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="flex flex-col gap-5">
          <div className="card-oxy flex gap-4 p-6" data-testid="service-diagnostic">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><Search className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display font-bold text-slate-900">Diagnostic</h3>
              <p className="mt-1 text-sm text-slate-600">On identifie la panne avant toute réparation. Tarif confirmé après diagnostic, sans mauvaise surprise.</p>
            </div>
          </div>
          <div className="card-oxy flex gap-4 p-6" data-testid="service-it">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><MonitorSmartphone className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display font-bold text-slate-900">Assistance informatique</h3>
              <p className="mt-1 text-sm text-slate-600">Aide à la configuration, transfert de données, sauvegardes, nettoyage et conseils sur vos appareils.</p>
            </div>
          </div>
          <div className="card-oxy flex gap-4 p-6" data-testid="service-protection">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><ShieldCheck className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display font-bold text-slate-900">Protections & pose</h3>
              <p className="mt-1 text-sm text-slate-600">Vitres trempées, coques renforcées — posées avec soin directement en boutique.</p>
            </div>
          </div>
          <div className="card-oxy flex gap-4 p-6" data-testid="service-accessories">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><Headphones className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display font-bold text-slate-900">Accessoires</h3>
              <p className="mt-1 text-sm text-slate-600">Audio, chargeurs, câbles, manettes… sélectionnés et testés par nos soins.</p>
              <Link to="/catalogue" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-gold-dark hover:underline">
                Voir le catalogue <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-3xl bg-ink p-6 text-white sm:p-10" data-testid="service-admin-print">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold text-ink"><Printer className="h-6 w-6" /></span>
            <h2 className="mt-4 font-display text-2xl font-bold">Impression & aides administratives</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Un document à imprimer, scanner ou envoyer ? Une démarche en ligne qui vous bloque ?
              On vous aide sur place, simplement.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-200">
              <li className="flex items-center gap-2"><FileText className="h-4 w-4 text-gold" /> Impressions & photocopies (noir/blanc et couleur)</li>
              <li className="flex items-center gap-2"><FileText className="h-4 w-4 text-gold" /> Numérisation et envoi de documents</li>
              <li className="flex items-center gap-2"><FileText className="h-4 w-4 text-gold" /> Aide aux démarches administratives en ligne</li>
              <li className="flex items-center gap-2"><FileText className="h-4 w-4 text-gold" /> CV, courriers, formulaires</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-gold/30 bg-white/5 p-6">
            <p className="font-display text-lg font-bold text-gold">Comment ça marche ?</p>
            <ol className="mt-4 space-y-3 text-sm text-slate-300">
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold font-display text-xs font-bold text-ink">1</span>Apportez votre document (clé USB, téléphone, papier)</li>
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold font-display text-xs font-bold text-ink">2</span>On s'occupe de tout, devant vous</li>
              <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold font-display text-xs font-bold text-ink">3</span>Vous repartez avec vos documents</li>
            </ol>
            <Link to="/contact" data-testid="service-print-cta" className="btn-gold mt-6 w-full !py-2.5 text-sm">Nous contacter</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
